import { useTranslation } from 'react-i18next';
import { CreateGameDialog } from '@admin/components/games/create-game-dialog.tsx';
import { UpdateGameDialog } from '@admin/components/games/update-game-dialog.tsx';
import { GamepadIcon } from 'lucide-react';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useGameColumns } from '@admin/components/games/game-columns.tsx';
import { toast } from 'sonner';
import { useState } from 'react';
import {
  useApiGamesGetCollection,
  apiGamesIdGet,
  useApiAdmingamesIdDelete
} from '@r/api/generated/client.ts';
import type {
  GameGameCollectionReadModel,
  GameGameReadModel
} from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Game = GameGameCollectionReadModel & {
  createdBy?: { user: { fullname: string } };
  link?: string;
};

type FullGame = GameGameReadModel & {
  createdBy?: { user: { fullname: string } };
};

const EMPTY_GAMES: Game[] = [];

export function Games() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [selectedGame, setSelectedGame] = useState<FullGame | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const gamesListQuery = useApiGamesGetCollection({ 'order[createdAt]': 'desc' });
  useOrvalQueryErrorToast(gamesListQuery, t('toast.error.label'));

  const handleSuccess = () => {
    gamesListQuery.refetch();
  };

  const deleteGameMutation = useApiAdmingamesIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('games.delete.success'));
        gamesListQuery.refetch();
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('games.delete.error'));
      }
    }
  });

  const handleDelete = (game: Game) => {
    toast(t('games.delete.confirm', { label: game.label }), {
      action: {
        label: t('label.confirm'),
        onClick: () => deleteGameMutation.mutate({ id: game.id.toString() })
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  const handleEditDialogOpenChange = (open: boolean) => {
    setIsEditDialogOpen(open);
    if (!open) {
      gamesListQuery.refetch();
    }
  };

  const handleEdit = async (game: Game) => {
    try {
      const fullGame = await apiGamesIdGet(game.id.toString());
      setSelectedGame(fullGame as FullGame);
      setIsEditDialogOpen(true);
    } catch (error) {
      showOrvalErrorToast(error, t('games.update.error'));
    }
  };

  const { table } = useDataTable({
    data: gamesListQuery.data ?? EMPTY_GAMES,
    columns: useGameColumns(handleDelete, handleEdit),
    pageCount: 1,
    enableAdvancedFilter: false
  });

  return (
    <div className="space-y-10">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-4xl font-bold">{t('menu.admin.animations.management')}</h1>
        <CreateGameDialog onCreated={handleSuccess} />
      </div>

      <div className="space-y-2.5">
        <div className="flex items-center space-x-2.5 text-2xl">
          <GamepadIcon className="w-6 h-6" />
          <p className="font-bold">{t('games.list.title')}</p>
        </div>

        {gamesListQuery.isLoading && (
          <DataTableSkeleton columnCount={4} cellWidths={['4rem', '20rem', '12rem', '12rem']} />
        )}

        {gamesListQuery.isSuccess && gamesListQuery.data.length === 0 && (
          <p className="text-muted-foreground">{t('games.list.empty')}</p>
        )}

        {gamesListQuery.isSuccess && gamesListQuery.data.length > 0 && (
          <DataTable table={table} pagination={false} />
        )}
      </div>

      <UpdateGameDialog
        game={selectedGame}
        isOpen={isEditDialogOpen}
        onOpenChange={handleEditDialogOpenChange}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
